/**
 * Copyright 2023-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import {
  Checkbox,
  ExtendedTooltip,
  FlexContainer,
  SelectButton,
  Text,
  theme,
} from "@nordcloud/gnui";
import { logErrorCodeCheckboxes } from "~/constants";
import { useQueryState } from "~/hooks";
import { getTitleText } from "~/tools";
import { useReportState } from "../ReportProvider";
import { FilterType, EventActionResourceReportsQuery } from "../types";
import {
  CheckboxWrapper,
  FilterCount,
  FilterLabel,
  StyledMultipleSelect,
} from "./styles";

export function ReportFilters() {
  const { reportState, updateReportState } = useReportState();
  const { queryState, updateQueryState } =
    useQueryState<EventActionResourceReportsQuery>();

  const selectedLogOutputs = queryState.logOutputs ?? [];

  const handleLogOutputChange = (value: string) => {
    const current = queryState.logOutputs ?? [];
    const next = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value];
    updateQueryState({
      logOutputs: next.length > 0 ? next : undefined,
      page: "1",
    });
  };

  const allPackagesCount =
    (reportState.packagesCount?.available ?? 0) +
    (reportState.packagesCount?.failed ?? 0) +
    (reportState.packagesCount?.installed ?? 0) +
    (reportState.packagesCount?.pendingReboot ?? 0) +
    (reportState.packagesCount?.removed ?? 0) +
    (reportState.packagesCount?.updated ?? 0);

  const reportFilters = [
    {
      label: "all",
      value: FilterType.ALL,
      count: allPackagesCount,
    },
    {
      label: "installed",
      value: FilterType.INSTALLED,
      description: "New package installed",
      count: reportState.packagesCount?.installed ?? 0,
    },
    {
      label: "pendingReboot",
      value: FilterType.PENDING_REBOOT,
      description: "Packages that require a reboot to be fully applied",
      count: reportState.packagesCount?.pendingReboot ?? 0,
    },
    {
      label: "updated",
      value: FilterType.UPDATED,
      description: "Existing package has been updated with the new version",
      count: reportState.packagesCount?.updated ?? 0,
    },
    {
      label: "available",
      value: FilterType.AVAILABLE,
      description:
        "Package is available for installation (currently not installed)",
      count: reportState.packagesCount?.available ?? 0,
    },
    {
      label: "removed",
      value: FilterType.REMOVED,
      description: "Package was uninstalled",
      count: reportState.packagesCount?.removed ?? 0,
    },
    {
      label: "failed",
      value: FilterType.FAILED,
      description:
        "An error occurred while trying to install/update the package",
      count: reportState.packagesCount?.failed ?? 0,
    },
  ];

  return (
    <>
      <FlexContainer
        direction="column"
        alignItems="flex-start"
        mb={theme.spacing.spacing03}
      >
        <Text
          size="sm"
          weight="medium"
          tag="div"
          color={theme.color.text.text02}
          mb={theme.spacing.spacing02}
        >
          Update Status
        </Text>
        <StyledMultipleSelect size="small">
          {reportFilters.map((filter) => (
            <ExtendedTooltip
              caption={filter?.description}
              position="start"
              key={filter.value}
            >
              <SelectButton
                name={filter.value}
                value={filter.value}
                labelText={
                  <FilterLabel>
                    <span>{getTitleText(filter.label)}</span>
                    <FilterCount>{filter.count}</FilterCount>
                  </FilterLabel>
                }
                isActive={reportState.filterQuery === filter.value}
                onClick={() => {
                  updateReportState({ filterQuery: filter.value });
                  updateQueryState({ page: "1" });
                }}
              />
            </ExtendedTooltip>
          ))}
        </StyledMultipleSelect>
      </FlexContainer>
      <FlexContainer direction="column" alignItems="flex-start">
        <Text
          size="sm"
          weight="medium"
          tag="div"
          color={theme.color.text.text02}
          mb={theme.spacing.spacing02}
        >
          Log Output
        </Text>
        <FlexContainer gap={theme.spacing.spacing04}>
          {logErrorCodeCheckboxes.map((checkbox) => (
            <CheckboxWrapper key={checkbox.value}>
              <Checkbox
                id={`log-output-${checkbox.value}`}
                name={`log-output-${checkbox.value}`}
                labelText={checkbox.label}
                checked={selectedLogOutputs.includes(checkbox.value)}
                onChange={() => handleLogOutputChange(checkbox.value)}
              />
            </CheckboxWrapper>
          ))}
        </FlexContainer>
      </FlexContainer>
    </>
  );
}
